// EditUser.jsx

import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { InputLabel, Autocomplete } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDAvatar from "components/MDAvatar";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import FormField from "layouts/applications/wizard/components/FormField";

import { useNavigate, useParams } from "react-router-dom";
import CrudService from "services/cruds-service";
import AuthService from "services/auth-service";

const EditUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [roles, setRoles] = useState([]);
  const [imageUrl, setImageUrl] = useState(null);
  const [fileState, setFileState] = useState(null);
  const [user, setUser] = useState({
    id: "",
    name: "",
    email: "",
    password: "",
    confirm: "",
    title: "",
    birthday: "",
    start:""
  });

  const [value, setValue] = useState(null);
  const [error, setError] = useState({
    name: false,
    email: false,
    password: false,
    confirm: false,
    role: false,
    error: false,
    textError: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const response = await CrudService.getRoles();
        setRoles(response.data);
      } catch (err) {
        console.error("Failed to fetch roles:", err);
      }
    })();
  }, []);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await CrudService.getUser(id);
        const data = res.data;
        const roleId = data.relationships.roles.data[0].id;
        const roleRes = await CrudService.getRole(roleId);
        const role = roleRes.data;

        setUser({
          id: data.id,
          name: data.attributes.name,
          email: data.attributes.email,
          title: data.attributes.title || "",
          birthday: data.attributes.birthday
              ? new Date(data.attributes.birthday).toISOString().split("T")[0]
              : "",
          start: data.attributes.start
              ? new Date(data.attributes.start).toISOString().split("T")[0]
              : "",
          password: "",
          confirm: "",
        });

        setValue(role);
        setImageUrl(data.attributes.profile_image);
      } catch (err) {
        console.error("Failed to fetch user:", err);
      }
    })();
  }, [id]);

  const changeHandler = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const changeImageHandler = (e) => {
    const file = e.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append("attachment", file);
      setFileState(formData);
      setImageUrl(URL.createObjectURL(file));
    }
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    const mailFormat = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (user.name.trim() === "")
      return setError({ ...error, name: true, textError: "Name cannot be empty" });

    if (!mailFormat.test(user.email.trim()))
      return setError({ ...error, email: true, textError: "Invalid email address" });

    if (!value?.id)
      return setError({ ...error, role: true, textError: "Role selection is required" });

    // If password fields are filled, update password
    if (user.password || user.confirm) {
      if (user.password.length < 8)
        return setError({ ...error, password: true, textError: "Password must be at least 8 characters" });

      if (user.password !== user.confirm)
        return setError({ ...error, confirm: true, textError: "Passwords do not match" });

      try {
        await AuthService.updateProfile({
          data: {
            attributes: {
              password: user.password,
              password_confirmation: user.confirm,
            },
          },
        });
      } catch (err) {
        return setError({
          ...error,
          error: true,
          textError: err?.response?.data?.message || "Password update failed",
        });
      }
    }

    // Upload profile image if selected
    let imageUrlToUpdate = imageUrl;
    if (fileState && user.id) {
      try {
        const upload = await CrudService.imageUpload(fileState, user.id.toString());
        imageUrlToUpdate = `${process.env.REACT_APP_IMAGES}${upload.url}`;
      } catch (err) {
        return setError({
          ...error,
          error: true,
          textError: "Image upload failed",
        });
      }
    }


    // Prepare updated user payload
    const updatedUser = {
      data: {
        id: user.id.toString(),
        type: "users",
        attributes: {
          name: user.name,
          email: user.email,
          title: user.title,
          birthday: user.birthday,
          start: user.start,
          profile_image: imageUrlToUpdate,
        },
        relationships: {
          roles: {
            data: [{ id: value.id.toString(), type: "roles" }],
          },
        },
      },
    };

    try {
      await CrudService.updateUser(updatedUser, user.id.toString());
      navigate("/user/user-management", {
        state: { value: true, text: "User updated successfully" },
      });
    } catch (err) {
      setError({
        ...error,
        error: true,
        textError: err?.response?.data?.message || "Update failed",
      });
    }
  };

  return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox mt={5} mb={9}>
          <Grid container justifyContent="center">
            <Grid item xs={12} lg={8}>
              <MDBox mt={6} mb={8} textAlign="center">
                <MDTypography variant="h3" fontWeight="bold">Edit User</MDTypography>
                <MDTypography variant="h5" color="secondary">
                  Modify the user’s information as needed.
                </MDTypography>
              </MDBox>
              <Card>
                <MDBox component="form" onSubmit={submitHandler} encType="multipart/form-data" px={3} my={4}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormField
                          label="Name"
                          name="name"
                          value={user.name}
                          onChange={changeHandler}
                          error={error.name}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormField
                          label="Email"
                          name="email"
                          value={user.email}
                          onChange={changeHandler}
                          error={error.email}
                          inputProps={{ type: "email" }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <MDInput
                          variant="standard"
                          label="New Password"
                          type="password"
                          name="password"
                          value={user.password}
                          onChange={changeHandler}
                          fullWidth
                          error={error.password}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <MDInput
                          variant="standard"
                          label="Confirm Password"
                          type="password"
                          name="confirm"
                          value={user.confirm}
                          onChange={changeHandler}
                          fullWidth
                          error={error.confirm}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormField
                          label="Title"
                          name="title"
                          value={user.title}
                          onChange={changeHandler}
                      />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <MDInput
                          variant="standard"
                          label="Birthday"
                          type="date"
                          name="birthday"
                          value={user.birthday}
                          onChange={changeHandler}
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <MDInput
                          variant="standard"
                          label="Start"
                          type="date"
                          name="start"
                          value={user.start}
                          onChange={changeHandler}
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>

                  <MDBox mt={3}>
                    <Autocomplete
                        options={roles}
                        value={value}
                        getOptionLabel={(option) => option?.attributes?.name ?? ""}
                        onChange={(e, newVal) => setValue(newVal)}
                        renderInput={(params) => <FormField {...params} label="Role" />}
                    />
                  </MDBox>

                  <MDBox mt={4} display="flex" alignItems="center" justifyContent="space-between">
                    <MDBox>
                      <InputLabel>Profile Image</InputLabel>
                      <MDInput type="file" onChange={changeImageHandler} accept="image/*" fullWidth />
                    </MDBox>
                    {imageUrl && (
                        <MDAvatar src={imageUrl} alt="profile" size="xxl" shadow="sm" />
                    )}
                  </MDBox>

                  {error.error && (
                      <MDTypography variant="caption" color="error" pt={2}>
                        {error.textError}
                      </MDTypography>
                  )}

                  <MDBox mt={4} display="flex" justifyContent="flex-end">
                    <MDButton variant="gradient" color="dark" onClick={() => navigate("/user/user-management")}>Back</MDButton>
                    <MDBox mx={1} />
                    <MDButton type="submit" variant="gradient" color="dark">Save</MDButton>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
        <Footer />
      </DashboardLayout>
  );
};

export default EditUser;
